<template>
  <div class="rb-trends-table-wrapper mt-4" :class="{ 'rb-compact': injectedReportData }">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="rb-trends-title mb-0">{{ $t('ReportBuilder.table.detailedResults') }}</h5>
      <el-tooltip v-if="tableData.length > 1" :content="sortTooltip" placement="top">
        <el-button size="small" circle plain @click="cycleSort">
          <i :class="sortIconClass"></i>
        </el-button>
      </el-tooltip>
    </div>

    <div class="rb-table-container graspil-scroll">
      <el-table
        v-if="tableData.length > 0"
        :data="pageRows"
        style="width: 100%"
        height="100%"
        header-cell-class-name="rb-table-header"
        :cell-style="cellStyle"
      >
        <!-- Visibility Toggle -->
        <el-table-column :width="injectedReportData ? 36 : 50" align="center">
          <template #default="scope">
            <el-checkbox
              :size="injectedReportData ? 'small' : 'default'"
              :model-value="!hiddenDatasets.includes(scope.row.rowIndex)"
              @change="toggleVisibility(scope.row.rowIndex)"
            />
          </template>
        </el-table-column>

        <!-- Series Name & Aggregation -->
        <el-table-column
          :label="$t('ReportBuilder.table.series')"
          min-width="180"
        >
          <template #default="scope">
            <div class="series-name-wrapper">
              <!-- Dashboard widgets: no separate "Цвет" column — the swatch (and its
                   style popover) sits inline before the label instead. Read-only contexts
                   (view page, dashboard) drop the swatch entirely — the row's left border
                   (see cellStyle) carries the color indication instead. -->
              <SeriesColorPicker
                v-if="injectedReportData && editable"
                small
                :color="scope.row.color"
                :line-width="scope.row.lineWidth"
                :line-style="scope.row.lineStyle"
                :line-tension="scope.row.lineTension"
                :show-points="scope.row.showPoints"
                :fill="scope.row.fill"
                :chart-type="scope.row.chartType"
                :show-chart-type="showChartTypeToggle && isSeriesRow(scope.row)"
                :palette="palette"
                :line-widths="lineWidths"
                :name="pickerName(scope.row)"
              :name-placeholder="pickerPlaceholder(scope.row)"
              resettable
              @update="patch => updateStyle(scope.row, patch)"
              />
              <span class="series-name">{{ scope.row.label }}</span>
              <el-tag
                v-if="scope.row.isFormula"
                size="small"
                type="warning"
                effect="plain"
                class="agr-badge"
              >
                f(x)
              </el-tag>
              <el-tag
                v-else-if="scope.row.agr"
                size="small"
                type="info"
                effect="plain"
                class="agr-badge"
              >
                {{ getAggregationLabel(scope.row.agr) }}
              </el-tag>
            </div>
          </template>
        </el-table-column>

        <!-- Grouping Column -->
        <el-table-column
          v-if="hasGroupings"
          :label="$t('ReportBuilder.action.groupBy')"
          min-width="150"
        >
          <template #default="scope">
            <span class="grouping-text">{{ categoryLabel(reportData.visual, scope.row.separators) }}</span>
          </template>
        </el-table-column>

        <!-- Color / Style: only in the standalone builder — dashboard widgets show
             the swatch inline in the Series column instead (see above). Read-only
             contexts drop this column too — no space spent on a swatch you can't click. -->
        <el-table-column v-if="!injectedReportData && editable" :label="$t('ReportBuilder.table.color')" width="80" align="center">
          <template #default="scope">
            <SeriesColorPicker
              :color="scope.row.color"
              :line-width="scope.row.lineWidth"
              :line-style="scope.row.lineStyle"
              :line-tension="scope.row.lineTension"
              :show-points="scope.row.showPoints"
              :fill="scope.row.fill"
              :chart-type="scope.row.chartType"
              :show-chart-type="showChartTypeToggle && isSeriesRow(scope.row)"
              :palette="palette"
              :line-widths="lineWidths"
              :name="pickerName(scope.row)"
              :name-placeholder="pickerPlaceholder(scope.row)"
              resettable
              @update="patch => updateStyle(scope.row, patch)"
            />
          </template>
        </el-table-column>

        <!-- Dynamic Date Columns -->
        <el-table-column
          v-for="(label, index) in columnLabels"
          :key="index"
          :label="label"
          align="center"
          :min-width="injectedReportData ? 64 : 100"
        >
          <template #default="scope">
            <div class="cell-value">{{ scope.row.data ? scope.row.data[index] : '' }}</div>
            <!-- Сравнение с другим периодом: дельта под значением, абсолют — в подсказке -->
            <div
              v-if="cellDelta(scope.row, index)"
              class="cell-delta"
              :class="'cell-delta--' + cellDelta(scope.row, index).dir"
              :title="$t('ReportBuilder.compare.kpiHint', {
                value: cellDelta(scope.row, index).prev,
                period: comparePeriodShortLabel
              })"
            >
              {{ cellDelta(scope.row, index).text }}
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div v-else class="text-center py-4 text-muted">
        {{ $t('ReportBuilder.table.noData') }}
      </div>
    </div>

    <div v-if="tableData.length > pageSize" class="d-flex justify-content-end mt-3">
      <el-pagination
        :current-page="currentPage"
        :page-size="pageSize"
        small
        background
        layout="total, prev, pager, next"
        :total="tableData.length"
        @current-change="currentPage = $event"
      />
    </div>
  </div>
</template>

<script>
import { useEvents } from "@/composables/useEvents";
import SeriesColorPicker from "./_series_color_picker.vue";
import { categoryLabel, getCategoryStyle, withCategoryStyle } from "@/helpers/reports/categoryStyles";
import { SERIES_PALETTE } from "@/helpers/reports/palette";
import { normalizeCompare, isCompareSupported, formatDeltaPct } from "@/helpers/reports/compare";

export default {
  name: "TrendsTable",
  components: { SeriesColorPicker },
  inject: {
    injectedReportData: {
      from: 'injectedReportData',
      default: null
    },
    editable: {
      from: 'injectedEditable',
      default: false
    },
    injectedExtraParams: {
      from: 'injectedExtraParams',
      default: null
    }
  },
  setup() {
    const { getEventName: resolveEventName } = useEvents();
    return { resolveEventName };
  },
  data() {
    return {
      palette: SERIES_PALETTE,
      lineWidths: [
        { value: 1 },
        { value: 2 },
        { value: 3 },
        { value: 4 },
      ],
      // 'none' — исходный порядок строк, 'desc'/'asc' — по сумме значений строки по всем датам
      sortDir: 'none',
      currentPage: 1,
      pageSize: 10
    }
  },
  watch: {
    // Новый прогон отчёта меняет состав строк — старый номер страницы может оказаться за пределами
    chartData() {
      this.currentPage = 1
    }
  },
  computed: {
    reportData() {
      return this.injectedReportData || this.$store.state.ReportFromBuilder
    },
    hiddenDatasets() {
      return this.reportData.hiddenDatasets || []
    },
    chartData() {
      return this.reportData.chartData || []
    },
    dateLabels() {
      if (this.chartData.length > 0) {
        return this.chartData[0].date || []
      }
      return []
    },
    isSqlReport() {
      return this.reportData.type === 'sql' || this.reportData.source_request?.type === 'sql'
    },
    // В SQL-отчёте ось X — произвольная колонка результата, датой её форматируем
    // только если она действительно датная (иначе '1' превратилось бы в «Янв 1»)
    isSqlDateAxis() {
      const x = this.reportData.source_request?.x
      if (!x) return false
      return (this.reportData.sqlColumns || []).some(c => c.name === x && c.type === 'date')
    },
    // Заголовки колонок со значениями. Круговая/столбцы/KPI в SQL схлопывают ряд
    // в одно число на серию — оси X у таких данных нет, колонка одна
    columnLabels() {
      const valueLabel = this.$t('ReportBuilder.table.value')
      if (this.dateLabels.length) {
        // Запрос без оси X даёт одну безымянную точку — подписываем её как значение
        return this.dateLabels.map(date => this.formatAxisLabel(date) || valueLabel)
      }
      const width = this.chartData[0]?.total?.length || 0
      return width === 1 ? [valueLabel] : []
    },
    hasGroupings() {
      if (!this.chartData || !this.chartData.length) return false
      return this.chartData.some(dataset => dataset.separators && dataset.separators.length > 0)
    },
    // Индивидуальный тип отображения (line/bar) серии имеет смысл только когда сам
    // отчёт line или bar — у area/cumulative/stacked/retention/utm_trends своя семантика
    showChartTypeToggle() {
      return this.reportData.type_chart === 'line' || this.reportData.type_chart === 'bar'
    },
    // Строки сравнительного периода, разложенные по ключу серии. По индексу сопоставлять
    // нельзя: порядок рядов в ответе задан суммой и между периодами отличается.
    compareRowsByKey() {
      if (!this.compareActive) return null;
      const map = {};
      for (const row of this.buildRows(this.reportData.compareData)) {
        map[this.seriesKey(row)] = row.data;
      }
      return map;
    },
    compareActive() {
      return !!normalizeCompare(this.reportData.visual)
        && isCompareSupported(this.reportData.type, this.reportData.type_chart)
        && Array.isArray(this.reportData.compareData)
        && this.reportData.compareData.length > 0;
    },
    comparePeriodShortLabel() {
      const mode = normalizeCompare(this.reportData.visual)?.mode;
      if (mode === 'previous_year') return this.$t('ReportBuilder.compare.previousYearShort');
      if (mode === 'custom') return this.$t('ReportBuilder.compare.customShort');
      return this.$t('ReportBuilder.compare.previousShort');
    },
    tableData() {
      const rows = this.buildRows(this.chartData);
      const cmp = this.compareRowsByKey;
      if (!cmp) return rows;
      return rows.map(row => ({ ...row, compareData: cmp[this.seriesKey(row)] || null }));
    },
    // Сортировка по сумме значений строки по всем датам — не трогает порядок строк,
    // пока пользователь явно её не включил (кнопка над таблицей)
    sortedRows() {
      if (this.sortDir === 'none') return this.tableData;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      return [...this.tableData].sort((a, b) => (this.rowTotal(a) - this.rowTotal(b)) * dir);
    },
    pageRows() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.sortedRows.slice(start, start + this.pageSize);
    },
    sortIconClass() {
      if (this.sortDir === 'desc') return 'fas fa-sort-down';
      if (this.sortDir === 'asc') return 'fas fa-sort-up';
      return 'fas fa-sort';
    },
    sortTooltip() {
      if (this.sortDir === 'none') return this.$t('ReportBuilder.table.sortByTotalDesc');
      if (this.sortDir === 'desc') return this.$t('ReportBuilder.table.sortByTotalAsc');
      return this.$t('ReportBuilder.table.sortDefault');
    }
  },
  methods: {
    rowTotal(row) {
      return (row.data || []).reduce((sum, v) => sum + ((v == null || isNaN(v)) ? 0 : v), 0);
    },
    cycleSort() {
      this.sortDir = this.sortDir === 'none' ? 'desc' : (this.sortDir === 'desc' ? 'asc' : 'none');
      this.currentPage = 1;
    },
    // Read-only contexts (view page, dashboard) have no color swatch to click, so the
    // series color shows as a left border on the name cell instead — same technique as
    // _utm_report.vue's utmCellStyle (border on <tr> is ignored by browsers, has to be
    // on the cell). Column 0 is always the checkbox, column 1 is always the Series name.
    cellStyle({ row, columnIndex }) {
      if (this.editable || columnIndex !== 1) return {};
      return { borderLeft: `2px solid ${row.color || 'transparent'}` };
    },
    // Дельта одной ячейки к тому же bucket'у сравнительного периода.
    // null — когда сравнение выключено, серия не нашлась или точки в ней нет.
    cellDelta(row, index) {
      const prev = row.compareData?.[index];
      if (prev === undefined || prev === null) return null;
      const current = row.data?.[index];
      if (current === undefined || current === null) return null;
      const text = formatDeltaPct(current, prev);
      if (!text) return null;
      return {
        text,
        dir: current > prev ? 'up' : (current < prev ? 'down' : 'flat'),
        prev
      };
    },
    // Ключ серии, одинаковый для обоих периодов: запрос (или формула) + значения разбивки
    seriesKey(row) {
      const sep = (row.separators || []).join('\x00');
      return (row.isFormula ? 'f' + row.formulaIndex : 'r' + row.index) + '\x00' + sep;
    },
    // Раньше это было тело computed tableData. Вынесено в метод, чтобы тем же кодом
    // построить строки сравнительного периода — включая формулы.
    buildRows(chartData) {
      if (!chartData || !chartData.length) return [];

      const rows = chartData.map((dataset, i) => {
        const reqIdx = (dataset.request_index !== undefined) ? dataset.request_index : i;
        const request = this.reportData.source_request.requests[reqIdx];

        let label = '';
        if (request?.type === 'sql_column') {
          // Серия SQL-отчёта — колонка результата, событий у неё нет
          label = request.label || request.column || this.$t('ReportBuilder.table.dataset', { n: reqIdx + 1 });
        } else if (request) {
          const rawLabel = request.label || request.event_name || this.getEventName(this.resolveTemplateEventId(request.event_id));
          label = this.translateEventLabel(rawLabel);
        } else {
          label = this.$t('ReportBuilder.table.dataset', { n: reqIdx + 1 });
        }

        return {
          index: reqIdx,
          rowIndex: i,
          label: label,
          // Собственное название серии для редактирования: `label` уже переведён и
          // может быть подставлен из имени события
          rawLabel: request?.label || '',
          agr: request?.agr || '',
          separators: dataset.separators || [],
          color: getCategoryStyle(this.reportData.visual, dataset.separators).color
            || (this.hasGroupings ? this.palette[i % this.palette.length] : (request?.color || this.palette[i % this.palette.length])),
          lineWidth: request?.lineWidth ?? 1,
          lineStyle: request?.lineStyle ?? 'solid',
          lineTension: request?.lineTension ?? 'smooth',
          showPoints: request?.showPoints ?? false,
          fill: request?.fill ?? false,
          chartType: request?.chartType || this.reportData.type_chart,
          data: dataset.total
        }
      });

      // Formula rows (computed client-side)
      const formulas = (this.reportData.source_request?.formulas || []).filter(f => f?.expression);
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const hasGroupingsForFormula = chartData.some(d => d.separators && d.separators.length > 0);

      for (let fi = 0; fi < formulas.length; fi++) {
        const formula = formulas[fi];

        if (hasGroupingsForFormula) {
          // Build per-group formula rows
          const groupMap = new Map();
          chartData.forEach((item, i) => {
            const reqIdx = (item.request_index !== undefined) ? item.request_index : i;
            const letter = letters[reqIdx];
            const sepKey = (item.separators || []).join('\x00');
            if (!groupMap.has(sepKey)) {
              groupMap.set(sepKey, { separators: item.separators || [], dataByLetter: {} });
            }
            const group = groupMap.get(sepKey);
            if (letter && !group.dataByLetter[letter]) {
              group.dataByLetter[letter] = item.total || [];
            }
          });
          const numPoints = chartData[0].total?.length || 0;
          groupMap.forEach((group) => {
            const formulaRowData = [];
            for (let i = 0; i < numPoints; i++) {
              const varMap = {};
              for (const [letter, values] of Object.entries(group.dataByLetter)) {
                varMap[letter] = values[i] ?? 0;
              }
              formulaRowData.push(this.evaluateFormula(formula.expression, varMap));
            }
            const sepSuffix = group.separators.length > 0 ? ` - ${group.separators.join(' - ')}` : '';
            rows.push({
              index: -(fi + 1),
              formulaIndex: fi,
              rowIndex: rows.length,
              label: (formula.label || 'Formula') + sepSuffix,
              rawLabel: formula.label || '',
              agr: '',
              separators: group.separators,
              color: formula.color || '#E6A23C',
              lineWidth: formula.lineWidth ?? 1,
              lineStyle: formula.lineStyle ?? 'dashed',
              lineTension: formula.lineTension ?? 'smooth',
              showPoints: formula.showPoints ?? false,
              fill: formula.fill ?? false,
              chartType: formula.chartType || this.reportData.type_chart,
              data: formulaRowData,
              isFormula: true
            });
          });
        } else {
          const requestDataMap = {};
          chartData.forEach((item, i) => {
            const reqIdx = (item.request_index !== undefined) ? item.request_index : i;
            const letter = letters[reqIdx];
            if (letter && requestDataMap[letter] === undefined) {
              requestDataMap[letter] = item.total || [];
            }
          });
          const numPoints = chartData[0].total?.length || 0;
          const formulaRowData = [];
          for (let i = 0; i < numPoints; i++) {
            const varMap = {};
            for (const [letter, values] of Object.entries(requestDataMap)) {
              varMap[letter] = values[i] ?? 0;
            }
            formulaRowData.push(this.evaluateFormula(formula.expression, varMap));
          }
          rows.push({
            index: -(fi + 1),
            formulaIndex: fi,
            rowIndex: rows.length,
            label: formula.label || 'Formula',
            rawLabel: formula.label || '',
            agr: '',
            separators: [],
            color: formula.color || '#E6A23C',
            lineWidth: formula.lineWidth ?? 1,
            lineStyle: formula.lineStyle ?? 'dashed',
            lineTension: formula.lineTension ?? 'smooth',
            showPoints: formula.showPoints ?? false,
            fill: formula.fill ?? false,
            chartType: formula.chartType || this.reportData.type_chart,
            data: formulaRowData,
            isFormula: true
          });
        }
      }

      return rows;
    },
    toggleVisibility(rowIndex) {
      if (this.injectedReportData) {
        if (!Array.isArray(this.injectedReportData.hiddenDatasets)) this.injectedReportData.hiddenDatasets = []
        const hidden = this.injectedReportData.hiddenDatasets
        const pos = hidden.indexOf(rowIndex)
        if (pos === -1) hidden.push(rowIndex)
        else hidden.splice(pos, 1)
        return
      }
      this.$store.commit('ReportFromBuilder/toggleDatasetVisibility', rowIndex)
    },
    categoryLabel,
    // Строка описывает серию целиком (событие/формулу), а не одно значение разбивки.
    // При разбивке цвет и подпись принадлежат значению, а не запросу: серий больше,
    // чем записей в requests, и раньше пикер писал в request, который график игнорировал.
    isSeriesRow(row) {
      return !row.separators || row.separators.length === 0 || row.isFormula;
    },
    customCategoryLabel(row) {
      return getCategoryStyle(this.reportData.visual, row.separators).label;
    },
    // Что редактируется в поле «Подпись»: у строки с разбивкой — название значения,
    // у обычной серии — её собственное название (иначе поля бы просто не было)
    pickerName(row) {
      return this.isSeriesRow(row) ? (row.rawLabel || '') : (this.customCategoryLabel(row) || '');
    },
    pickerPlaceholder(row) {
      return this.isSeriesRow(row) ? row.label : row.separators.join(' - ');
    },
    updateStyle(row, patch) {
      let rest = patch;

      // Цвет и подпись у строки с разбивкой принадлежат значению, остальное
      // (толщина, тип линии, точки, заливка) по-прежнему настраивается на серию целиком
      if (!this.isSeriesRow(row)) {
        const { color, label, ...other } = patch;
        if (color !== undefined || label !== undefined) {
          const stylePatch = {};
          if (color !== undefined) stylePatch.color = color;
          if (label !== undefined) stylePatch.label = label;
          if (this.injectedReportData) {
            this.injectedReportData.visual = withCategoryStyle(this.injectedReportData.visual, row.separators, stylePatch);
          } else {
            this.$store.commit('ReportFromBuilder/setCategoryStyle', { separators: row.separators, patch: stylePatch });
          }
        }
        if (!Object.keys(other).length) return;
        rest = other;
      }

      const index = row.index;
      if (index < 0) {
        // Formula row: index = -(fi + 1), so fi = -(index + 1)
        const fi = -(index + 1);
        if (this.injectedReportData) {
          this.patchLocalCollection('formulas', fi, rest);
          return;
        }
        this.$store.commit('ReportFromBuilder/updateFormula', { index: fi, patch: rest });
        return;
      }
      // В виджете конфиг живёт в его собственном состоянии — глобальный стор
      // конструктора здесь принадлежит другому отчёту
      if (this.injectedReportData) {
        this.patchLocalCollection('requests', index, rest);
        return;
      }
      this.$store.commit('ReportFromBuilder/updateRequest', { index, patch: rest });
    },
    patchLocalCollection(field, index, patch) {
      const items = this.injectedReportData.source_request?.[field];
      if (!Array.isArray(items) || !items[index]) return;
      items[index] = { ...items[index], ...patch };
    },
    translateEventLabel(label) {
      if (!label) return label;
      return label.split(', ').map(name => {
        const trimmed = name.trim();
        const key = `Components.EventSelector.CustomEvents.${trimmed}.name`;
        return this.$te(key) ? this.$t(key) : trimmed;
      }).join(', ');
    },
    getEventName(eventId) {
      if (eventId === 'all' || eventId === null) {
        return this.$t("Components.EventSelector.AnyEvent");
      }
      if (Array.isArray(eventId)) {
        return eventId.map(id => this.getEventName(id)).join(', ');
      }
      const raw = this.resolveEventName(eventId);
      return this.translateEventLabel(raw);
    },
    // See _chart.vue's resolveTemplateEventId — same placeholder-to-real-id resolution.
    resolveTemplateEventId(eventId) {
      if (typeof eventId !== 'string' || !eventId.startsWith(':')) return eventId;
      const params = this.injectedExtraParams?.value;
      const resolved = params?.[eventId];
      if (resolved === undefined || resolved === null) return eventId;
      if (Array.isArray(resolved)) return resolved.length === 1 ? resolved[0] : (resolved.length ? resolved : eventId);
      return resolved;
    },
    evaluateFormula(expression, varMap) {
      if (!/^[A-Za-z0-9+\-*/().\\s\s]+$/.test(expression)) return null;
      let expr = expression.toUpperCase();
      for (const [letter, value] of Object.entries(varMap)) {
        const safeVal = (value === null || value === undefined || isNaN(value)) ? 0 : value;
        expr = expr.replace(new RegExp(`\\b${letter}\\b`, 'g'), `(${safeVal})`);
      }
      try {
        // eslint-disable-next-line no-new-func
        const result = new Function(`return ${expr}`)();
        if (!isFinite(result)) return null;
        return Math.round(result * 10000) / 10000;
      } catch {
        return null;
      }
    },
    getAggregationLabel(agr) {
      const labels = {
        'total': this.$t('ReportBuilder.action.total'),
        'total_uniq_user_id': this.$t('ReportBuilder.action.uniqueUsers'),
        'total_uniq_session_id': this.$t('ReportBuilder.action.uniqueSessions'),
        'total_uniq_app_session_id': this.$t('ReportBuilder.action.uniqueAppSessions'),
        'avg_user_id': this.$t('ReportBuilder.action.avg'),
        'min_user_id': this.$t('ReportBuilder.action.min'),
        'max_user_id': this.$t('ReportBuilder.action.max'),
        'median_user_id': this.$t('ReportBuilder.action.median'),
      };
      return labels[agr] || agr;
    },
    formatAxisLabel(value) {
      if (this.isSqlReport) {
        return this.isSqlDateAxis ? this.formatSqlDate(value) : String(value ?? '');
      }
      return this.formatDate(value);
    },
    // У SQL-запроса нет granularity — шаг оси виден по самому значению
    formatSqlDate(value) {
      const raw = String(value ?? '');
      const date = new Date(raw.replace(' ', 'T'));
      if (isNaN(date.getTime())) return raw;
      const monthNamesShort = this.$tm('ReportBuilder.constructor.monthNamesShort');
      const day = monthNamesShort[date.getMonth()] + ' ' + date.getDate();
      if (!/[T ]\d{2}:\d{2}/.test(raw)) return day;
      return day + ' ' + String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0');
    },
    formatDate(dateStr) {
      const date = new Date(dateStr);
      const granularity = this.reportData.source_request.granularity || 'day';
      const monthNamesShort = this.$tm('ReportBuilder.constructor.monthNamesShort');

      if (granularity === 'month') {
        return monthNamesShort[date.getMonth()] + ' ' + date.getFullYear();
      }
      if (granularity === 'week' || granularity === 'day') {
        return monthNamesShort[date.getMonth()] + ' ' + date.getDate();
      }
      if (granularity === 'hour') {
        return String(date.getHours()).padStart(2, '0') + ':00';
      }
      if (granularity === 'minute') {
        return String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0');
      }
      return dateStr;
    }
  }
}
</script>

<style scoped>
.rb-trends-table-wrapper {
  background: var(--ui-bg-card, #fff);
  border-radius: 12px;
  border: 1px solid var(--ui-border, #e4e7ed);
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Embedded in a dashboard widget: no nested card chrome, tighter spacing, and the
   heading collapses to a small uppercase caption instead of a full h5. Dashboard
   grid tiles don't reliably propagate height:100% down to widget internals, so
   this variant sizes itself via flex instead (needs min-height:0 to actually
   shrink/scroll within the tile rather than overflowing it). */
.rb-compact.rb-trends-table-wrapper {
  border: none;
  border-radius: 0;
  padding: 0;
  margin-top: 12px !important;
  height: auto;
  flex: 1 1 0;
  min-height: 0;
}

.rb-compact .d-flex.mb-3 {
  margin-bottom: 8px !important;
}

.rb-compact .rb-trends-title {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--ui-text-3, #909399);
}

.rb-compact :deep(.el-table) {
  font-size: 12px;
}

.rb-compact :deep(.el-table .cell) {
  padding-left: 6px;
  padding-right: 6px;
  line-height: 1.3;
}

/* Tight rows: trim vertical padding on both header and body cells */
.rb-compact :deep(.el-table td.el-table__cell),
.rb-compact :deep(.el-table th.el-table__cell) {
  padding: 4px 0;
}

/* Smaller, lighter column headers. Dates must stay on one line — the container
   scrolls horizontally (.rb-table-container) rather than wrapping "Июн 10" onto
   two lines. */
.rb-compact :deep(.el-table th.el-table__cell) {
  font-size: 11px;
  font-weight: 600;
}

.rb-compact :deep(.el-table th.el-table__cell .cell) {
  white-space: nowrap;
}

.rb-compact .series-name {
  font-size: 12px;
  font-weight: 500;
}

/* Series name + aggregation tag: let the tag wrap onto its own line under the name
   (as before) rather than forcing one line, which was clipping the tag's text. */
.rb-compact .series-name-wrapper {
  gap: 4px 6px;
}

.rb-compact .agr-badge {
  height: 16px;
  line-height: 14px;
  font-size: 10px;
  padding: 0 5px;
}

.rb-table-container {
  overflow-x: auto;
  overflow-y: auto;
  flex-grow: 1;
}

.series-name-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.series-name {
  font-weight: 500;
  color: var(--ui-text-1, #303133);
  font-size: 14px;
}

.agr-badge {
  text-transform: lowercase;
  font-size: 11px;
  height: 20px;
  line-height: 18px;
  padding: 0 6px;
  background-color: #f4f4f5;
  border-color: #e9e9eb;
  color: #909399;
}

.grouping-text {
  color: var(--ui-text-2, #606266);
  font-size: 13px;
}

/* Сравнение периодов: дельта под значением ячейки */
.cell-value {
  line-height: 1.2;
}

.cell-delta {
  margin-top: 1px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--ui-text-3, #909399);
  white-space: nowrap;
}

.cell-delta--up {
  color: #1f9d6b;
}

.cell-delta--down {
  color: #d95757;
}

/* В дашборд-виджете колонки узкие — дельту ужимаем, чтобы не рвала раскладку */
.rb-compact .cell-delta {
  font-size: 10px;
}
</style>

<style>
</style>
